import { Alert, Select, TextInput } from "@mantine/core";
import type { ReactElement } from "react";
import type { TFunction } from "i18next";

import { useAgentRoles } from "../agentRoles/useAgentRoles";
import { isRoleCriticalTool } from "../toolBlacklist/footgun";

// Форма apply `tool_blacklist` для ProposalCard. Зеркало
// `checklist_tweak` (checklistTweak.tsx). Человек подтверждает роль (Select)
// и имя инструмента —
// предложение LLM = рекомендация в `proposal_text`, не источник истины (см.
// спека 03-agents/roles/overrides § «Применение tool_blacklist»). Footgun-Alert
// предупреждает о роль-критичных инструментах (§ «Footgun») — единственная
// защита; запрет всё равно можно поставить.

// Чистая проверка готовности формы tool_blacklist к apply. Оба поля обязаны
// быть непустыми после trim'а (backend отвергает blank 422). Вынесена чистой
// функцией: Select onChange в jsdom не зовётся, держим логику отдельно для
// прямого юнита (как у isChecklistTweakReady). Роль приходит как `null` (не
// выбрана) → не готово.
export function isToolBlacklistReady(
  roleName: string | null,
  toolName: string,
): boolean {
  return roleName !== null && toolName.trim().length > 0;
}

// Форма tool_blacklist: Select роли + TextInput имени инструмента + footgun-
// Alert. Значения подтверждает человек; ProposalCard собирает из них тело
// apply (`buildApplyArgs`) после проверки `isToolBlacklistReady`.
export function ToolBlacklistForm({
  roleName,
  setRoleName,
  toolName,
  setToolName,
  busy,
  t,
}: {
  readonly roleName: string | null;
  readonly setRoleName: (value: string | null) => void;
  readonly toolName: string;
  readonly setToolName: (value: string) => void;
  readonly busy: boolean;
  readonly t: TFunction;
}): ReactElement {
  const roles = useAgentRoles();
  const roleOptions = (roles.data ?? []).map((role) => ({
    value: role.role_name,
    label: t(`agentRoles.role.${role.role_name}`, {
      defaultValue: role.role_name,
    }),
  }));
  const footgun =
    roleName !== null &&
    toolName.trim().length > 0 &&
    isRoleCriticalTool(roleName, toolName);

  return (
    <>
      <Select
        label={t("selfImprovement.blacklistRoleLabel")}
        description={t("selfImprovement.blacklistRoleHint")}
        placeholder={t("selfImprovement.blacklistRolePlaceholder")}
        data={roleOptions}
        value={roleName}
        onChange={setRoleName}
        disabled={busy || roles.isLoading}
        error={
          roles.isError ? t("selfImprovement.blacklistRolesError") : undefined
        }
      />
      <TextInput
        label={t("selfImprovement.blacklistToolLabel")}
        description={t("selfImprovement.blacklistToolHint")}
        placeholder={t("selfImprovement.blacklistToolPlaceholder")}
        value={toolName}
        onChange={(event) => {
          setToolName(event.currentTarget.value);
        }}
        disabled={busy}
      />
      {footgun ? (
        <Alert color="orange" title={t("toolBlacklist.footgun.title")}>
          {t("toolBlacklist.footgun.text")}
        </Alert>
      ) : null}
    </>
  );
}
